import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { debounce } from "lodash";
import { Controller } from "react-hook-form";

import { isBusinessProfileRegistered } from "../../constants/businessProfileStatusConstants.jsx";
import { useGetBusinessProfilesHandler } from "../../resources/businessProfiles/dataHandlers/getBusinessProfilesHandler";

const UNREGISTERED_PROFILE_MESSAGE =
  "This profile is not registered on Oman E-Invoicing Network, go on the profile section and register it.";

const SINGLE_LINE_SELECT_SX = {
  "& .MuiOutlinedInput-root": { flexWrap: "nowrap", overflow: "hidden" },
  "& .MuiAutocomplete-input": { minWidth: 430 },
  "& .MuiChip-root": { maxWidth: 590 },
};

export const getProfileVat = (profile) =>
  profile?.endpoint_id ||
  profile?.party_tax_scheme?.company_id ||
  profile?.company_id ||
  "";

export const getVatOptionLabel = (profile) => {
  const vat = getProfileVat(profile);
  const name = profile?.name || "";
  if (vat && name) {
    return `${vat} | ${name}`;
  }
  return vat || name || profile?.profile_id || "";
};

const getUnregisteredReason = (profile) =>
  isBusinessProfileRegistered(profile) ? "" : UNREGISTERED_PROFILE_MESSAGE;

export const keepRegisteredProfiles = (profiles) =>
  (profiles || []).filter(isBusinessProfileRegistered);

const renderCollapsedTags = (getLabel, getKey) => (value, getTagProps) => {
  const tags = value
    .slice(0, 1)
    .map((option, index) => (
      <Chip
        {...getTagProps({ index })}
        key={getKey(option, index)}
        label={getLabel(option)}
        size="small"
      />
    ));

  if (value.length > 1) {
    tags.push(
      <Typography
        key="more"
        variant="body2"
        sx={{ ml: 0.5, whiteSpace: "nowrap", color: "text.secondary" }}
      >
        +{value.length - 1}
      </Typography>,
    );
  }

  return tags;
};

const getProfileKey = (profile) => profile?.profile_id || profile?.id;

const mergeSelectedOptions = (options, selected) => {
  const selectedList = Array.isArray(selected)
    ? selected
    : selected
      ? [selected]
      : [];
  const keys = new Set(options.map(getProfileKey).filter(Boolean));
  const merged = [...options];

  selectedList.forEach((profile) => {
    const key = getProfileKey(profile);
    if (key && !keys.has(key)) {
      keys.add(key);
      merged.push(profile);
    }
  });

  return merged;
};

const VatProfileSelect = ({
  name,
  control,
  label = "VAT Number",
  errors,
  required = false,
  multiple = false,
  size = "small",
  fullWidth = true,
  sx = SINGLE_LINE_SELECT_SX,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 500);

    handler();
    return () => handler.cancel();
  }, [searchTerm]);

  const { businessProfiles, isLoading } = useGetBusinessProfilesHandler({
    searchQuery: {
      page: 0,
      page_size: 10,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    },
  });

  const vatOptions = useMemo(
    () => (businessProfiles || []).filter((profile) => getProfileVat(profile)),
    [businessProfiles],
  );

  return (
    <Controller
      name={name}
      control={control}
      rules={required ? { required: `${label} is required` } : undefined}
      render={({ field }) => (
        <Autocomplete
          multiple={multiple}
          size={size}
          fullWidth={fullWidth}
          sx={sx}
          disableCloseOnSelect={multiple}
          loading={isLoading}
          options={mergeSelectedOptions(
            vatOptions,
            multiple ? field.value || [] : field.value || null,
          )}
          filterOptions={(x) => x}
          getOptionLabel={getVatOptionLabel}
          getOptionDisabled={(option) => Boolean(getUnregisteredReason(option))}
          isOptionEqualToValue={(option, value) =>
            getProfileKey(option) === getProfileKey(value)
          }
          value={multiple ? field.value || [] : field.value || null}
          onChange={(_, value) => {
            if (multiple) {
              field.onChange(keepRegisteredProfiles(value));
              return;
            }
            if (value && getUnregisteredReason(value)) {
              return;
            }
            field.onChange(value);
          }}
          onInputChange={(_, value, reason) => {
            if (reason === "input") {
              setSearchTerm(value);
            }
            if (reason === "clear") {
              setSearchTerm("");
            }
          }}
          onBlur={field.onBlur}
          renderOption={(props, option) => {
            const { key, onClick, onMouseDown, ...optionProps } = props;
            const disabledReason = getUnregisteredReason(option);
            const blockDisabledSelection = (event) => {
              event.preventDefault();
              event.stopPropagation();
            };
            const optionLabel = (
              <>
                {disabledReason ? (
                  <ErrorOutlineIcon
                    sx={{
                      color: "error.main",
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                    aria-hidden
                  />
                ) : null}{" "}
                {getVatOptionLabel(option)}
              </>
            );

            return (
              <Box
                key={key}
                component="li"
                {...optionProps}
                onMouseDown={
                  disabledReason ? blockDisabledSelection : onMouseDown
                }
                onClick={disabledReason ? blockDisabledSelection : onClick}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  opacity: disabledReason ? 0.5 : 1,
                  cursor: disabledReason ? "not-allowed" : "pointer",
                }}
              >
                {disabledReason ? (
                  <Tooltip title={disabledReason} placement="right">
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        pointerEvents: "auto",
                      }}
                    >
                      {optionLabel}
                    </Box>
                  </Tooltip>
                ) : (
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {optionLabel}
                  </Box>
                )}
              </Box>
            );
          }}
          renderTags={
            multiple
              ? renderCollapsedTags(
                  (option) =>
                    getProfileVat(option) || getVatOptionLabel(option),
                  (option, index) => option?.profile_id || option?.id || index,
                )
              : undefined
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              required={required}
              error={!!errors}
              helperText={errors?.message || ""}
            />
          )}
        />
      )}
    />
  );
};

export default VatProfileSelect;
